import * as React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import makeStyles from '@material-ui/core/styles/makeStyles'
import InView from '@oakwood/oui/InView'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import Container from 'components/Container'
import Section from 'components/Section'
import SlideshowNavigation from 'components/SlideshowNavigation'
import Typography from 'components/Typography'

const TRANSITION_DELAY = 5000

export const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: -1,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  bounds: {
    ...theme.mixins.absolute(0),
    zIndex: -1,
  },
  main: theme.mixins.contain(720),
  quotes: {
    display: 'flex',
  },
  quote: {
    ...theme.mixins.verticalRhythm(4),
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    marginRight: '-100%',
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.complex * 2,
    }),
    opacity: 0,
    pointerEvents: 'none',
    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  quoteActive: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  mediaContainer: {
    width: 'auto',
    height: 32,
  },
  media: {
    width: 'inherit',
    height: 'inherit',
  },
  navigation: {
    marginTop: 'calc(var(--coa-section-spacing) / 2)',
  },
}))

function Quotes(props) {
  const { items } = props
  const classes = useStyles(props)

  const timeoutRef = React.useRef(null)
  const [counter, setCounter] = React.useState(0)

  const startCounter = React.useCallback(() => {
    timeoutRef.current = setInterval(() => {
      setCounter((prev) => prev + 1)
    }, TRANSITION_DELAY)
  }, [])

  const stopCounter = React.useCallback(() => {
    clearInterval(timeoutRef.current)
  }, [])

  const handlePrev = React.useCallback(() => {
    clearInterval(timeoutRef.current)
    setCounter((prev) => (prev === 0 ? items.length - 1 : prev - 1))
    startCounter()
  }, [startCounter, items])

  const handleNext = React.useCallback(() => {
    clearInterval(timeoutRef.current)
    setCounter((prev) => prev + 1)
    startCounter()
  }, [startCounter])

  React.useEffect(() => {
    return () => {
      stopCounter()
    }
  }, [stopCounter])

  const selectedIndex = counter % items.length

  return (
    <Section className={classes.root} gutters="padding">
      <InView className={classes.bounds} onEnter={startCounter} onExit={stopCounter} />

      <Container className={classes.main}>
        <div className={classes.quotes}>
          {items?.map((item, idx) => (
            <article
              key={idx}
              className={clsx(classes.quote, {
                [classes.quoteActive]: selectedIndex === idx,
              })}
            >
              {item.mediaSrc && (
                <MediaReveal className={classes.mediaContainer}>
                  <Media className={classes.media} src={item.mediaSrc} />
                </MediaReveal>
              )}

              {item.text ? (
                <Typography className={classes.text} variant="preamble">
                  {item.text}
                </Typography>
              ) : (
                <Typography
                  className={classes.text}
                  variant="preamble"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: item.content,
                  }}
                />
              )}
            </article>
          ))}
        </div>

        {items.length > 1 && (
          <SlideshowNavigation
            className={classes.navigation}
            listLength={items.length}
            onNextClick={handleNext}
            onPrevClick={handlePrev}
            selectedIndex={selectedIndex}
          />
        )}
      </Container>
    </Section>
  )
}

const itemType = PropTypes.shape({
  mediaSrc: PropTypes.string,
  text: PropTypes.string.isRequired,
})

Quotes.propTypes = {
  items: PropTypes.arrayOf(itemType).isRequired,
}

export default Quotes
